import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink, ShieldCheck } from 'lucide-react'; // Suggested: lucide-react for cleaner icons

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={containerVariants}
      className="relative bg-[#05070a] text-white pt-16 pb-8 overflow-hidden"
    >
      {/* Subtle Background Accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Brand Section - Takes 5/12 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-6">
            <div className="flex flex-col space-y-4">
              <motion.img
                whileHover={{ rotate: 5, scale: 1.05 }}
                src="/logo/Gujarat_APP_Association.jpg"
                alt="Logo"
                className="w-20 h-20 object-contain rounded-xl border-2 border-gold/30 p-1 bg-white/5"
              />
              <div>
                <h2 className="text-xl md:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gold via-yellow-200 to-gold">
                  THE GUJARAT STATE <br />
                  <span className="text-lg font-medium text-gray-100">ASSISTANT PUBLIC PROSECUTORS ASSOCIATION</span>
                </h2>
              </div>
            </div>
            <p className="text-gray-400 max-w-md leading-relaxed">
              Upholding the integrity of the prosecution service. We empower our members 
              through legal excellence, advocacy, and a shared commitment to the rule of law in Gujarat.
            </p>
            <div className="flex items-center gap-2 text-gold text-sm font-semibold">
              <ShieldCheck size={18} />
              <span>Official Association Portal</span>
            </div>
          </motion.div>

          {/* Links Sections - Takes 4/12 columns */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-8">
            <motion.div variants={itemVariants}>
              <h3 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Navigation</h3>
              <ul className="space-y-4">
                {['Home', 'About', 'Members', 'Contact'].map((item) => (
                  <li key={item}>
                    <Link 
                      to={item === 'Home' ? '/' : `/${item.toLowerCase()}`} 
                      className="text-gray-400 hover:text-gold hover:translate-x-1 transition-all duration-300 flex items-center gap-1 group"
                    >
                      <span className="w-0 group-hover:w-2 h-[1px] bg-gold transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Resources</h3>
              <ul className="space-y-4">
                {[
                  { name: 'High Court', url: 'https://gujarathighcourt.nic.in/' },
                  { name: 'Home Dept.', url: 'https://home.gujarat.gov.in/' },
                  { name: 'Gujarat Police', url: 'https://gujhome.gujarat.gov.in/' }
                ].map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-gray-400 hover:text-gold transition-colors flex items-center gap-1 group"
                    >
                      {link.name}
                      <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Section - Takes 3/12 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <h3 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Get In Touch</h3>
            <div className="space-y-4">
              <div className="group flex items-start gap-3">
                <div className="p-2 rounded-lg bg-gold/10 text-gold group-hover:bg-gold group-hover:text-black transition-colors duration-300">
                  <MapPin size={18} />
                </div>
                <span className="text-sm text-gray-400 leading-snug">
                  B/6 APP Office, Ahmedabad Rural Court, Navrangpura, Ahmedabad - 380014
                </span>
              </div>

              <a href="mailto:tusharbarot85@gmail.com" className="group flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gold/10 text-gold group-hover:bg-gold group-hover:text-black transition-colors duration-300">
                  <Mail size={18} />
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gold transition-colors">
                  tusharbarot85@gmail.com
                </span>
              </a>

              <a href="tel:+919879960984" className="group flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gold/10 text-gold group-hover:bg-gold group-hover:text-black transition-colors duration-300">
                  <Phone size={18} />
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gold transition-colors">
                  +91 98799 60984
                </span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          variants={itemVariants}
          className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left"
        >
          <div className="text-gray-500 text-xs">
            © {new Date().getFullYear()} GSAPPA. All rights reserved. Professional Association for Public Prosecutors.
          </div>
          
          <div className="text-xs text-gray-500">
            Designed & Maintained by{" "}
            <a
              href="https://www.kevalontechnology.in"
              className="text-gray-300 hover:text-gold font-medium transition-colors"
            >
              Kevalon Technology
            </a>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;